import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

import { authService, formatApiError } from "@/services/authService";

export const Projects = () => {
    const [projects, setProjects] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await authService.http.get("/projects");
                setProjects(data);
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
    }, []);

    return (
        <div data-testid="projects-page" className="px-6 lg:px-10 py-10">
            <div className="mx-auto max-w-[1100px]">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <span className="tc-eyebrow text-[#01696f]">Workspace · projects</span>
                        <h1 className="text-[26px] font-medium tracking-tight text-[#0e1416] mt-2">
                            All projects
                        </h1>
                    </div>
                    <Link
                        to="/app/projects/new"
                        data-testid="projects-new-cta"
                        className="inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-4 py-2.5 text-[13px] transition-colors"
                    >
                        <Plus size={14} strokeWidth={1.5} />
                        New project
                    </Link>
                </div>

                {error && (
                    <div className="border-l-2 border-[#a13434] pl-3 text-[12px] text-[#a13434] tc-mono mb-4">
                        {error}
                    </div>
                )}

                {projects && projects.length === 0 && (
                    <div className="border border-dashed border-[#d8dcdd] px-6 py-10 text-center">
                        <div className="tc-mono text-[11px] text-[#5a6669] tracking-widest uppercase mb-2">
                            0 projects
                        </div>
                        <p className="text-[13px] text-[#5a6669] max-w-[44ch] mx-auto leading-[1.55]">
                            No projects yet. Start with a new one.
                        </p>
                    </div>
                )}

                {projects && projects.length > 0 && (
                    <div className="border border-[#d8dcdd] bg-[#ffffff]">
                        <div className="grid grid-cols-12 px-4 py-2.5 border-b border-[#d8dcdd] tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">
                            <div className="col-span-5">Name</div>
                            <div className="col-span-2">Type</div>
                            <div className="col-span-3">Location</div>
                            <div className="col-span-2 text-right">Status</div>
                        </div>
                        <ul className="divide-y divide-[#d8dcdd]">
                            {projects.map((p) => (
                                <li key={p.id}>
                                    <Link
                                        to={`/app/projects/${p.id}`}
                                        data-testid={`projects-row-${p.id}`}
                                        className="grid grid-cols-12 px-4 py-3 items-center hover:bg-[#eef0ef] transition-colors"
                                    >
                                        <div className="col-span-5 text-[13.5px] text-[#0e1416]">
                                            {p.name}
                                        </div>
                                        <div className="col-span-2 tc-mono text-[11px] text-[#5a6669]">
                                            {p.project_type}
                                        </div>
                                        <div className="col-span-3 text-[12px] text-[#5a6669]">
                                            {p.location}
                                        </div>
                                        <div className="col-span-2 text-right tc-mono text-[10px] text-[#01696f] uppercase tracking-wider">
                                            {p.status}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ProjectNew = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        project_type: "mass_pour",
        location: "Hong Kong",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.name.trim()) {
            setError("Project name is required.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await authService.http.post("/projects", form);
            toast.success("Project created.");
            navigate(`/app/projects/${data.id}`, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="project-new-page" className="px-6 lg:px-10 py-10">
            <div className="mx-auto max-w-[640px]">
                <span className="tc-eyebrow text-[#01696f]">Workspace · new project</span>
                <h1 className="text-[26px] font-medium tracking-tight text-[#0e1416] mt-2 leading-[1.2]">
                    Create a new project.
                </h1>
                <p className="mt-3 text-[13.5px] text-[#5a6669] max-w-[60ch]">
                    Define the project context. Mix, geometry, and thermal
                    inputs are configured inside the project workspace in
                    Phase 2B.
                </p>

                <form
                    data-testid="project-new-form"
                    onSubmit={onSubmit}
                    className="mt-8 flex flex-col gap-6 border border-[#d8dcdd] bg-[#ffffff] p-7"
                >
                    <Field
                        id="name"
                        label="Project name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="PIER-A03"
                        required
                        testid="project-new-name"
                    />
                    <div className="flex flex-col gap-2">
                        <label
                            htmlFor="project_type"
                            className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase"
                        >
                            Project type
                        </label>
                        <select
                            id="project_type"
                            value={form.project_type}
                            onChange={update("project_type")}
                            data-testid="project-new-type"
                            className="bg-transparent border border-[#d8dcdd] focus:border-[#01696f] outline-none px-3 py-2.5 text-[14px] text-[#0e1416] transition-colors"
                        >
                            <option value="mass_pour">mass_pour</option>
                            <option value="phc_pile">phc_pile</option>
                            <option value="slab">slab</option>
                            <option value="wall">wall</option>
                            <option value="custom">custom</option>
                        </select>
                    </div>
                    <Field
                        id="location"
                        label="Location"
                        value={form.location}
                        onChange={update("location")}
                        placeholder="Hong Kong"
                        testid="project-new-location"
                    />

                    {error && (
                        <div
                            data-testid="project-new-error"
                            className="tc-mono text-[11px] text-[#a13434] border-l-2 border-[#a13434] pl-3"
                        >
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-[#d8dcdd]">
                        <Link
                            to="/app/projects"
                            className="tc-mono text-[12px] text-[#5a6669] hover:text-[#0e1416] transition-colors"
                        >
                            ← Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="project-new-submit"
                            className="group inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-60 text-[#ffffff] px-4 py-2.5 text-[13px] transition-colors"
                        >
                            <span>{submitting ? "Creating…" : "Create project"}</span>
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Field = ({ id, label, value, onChange, placeholder, required, testid }) => (
    <div className="flex flex-col gap-2">
        <label
            htmlFor={id}
            className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase"
        >
            {label}
            {required && <span className="text-[#01696f]"> *</span>}
        </label>
        <input
            id={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            data-testid={testid}
            className="w-full bg-transparent border-0 border-b border-[#d8dcdd] focus:border-[#01696f] outline-none px-0 py-2 text-[14px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors"
        />
    </div>
);
